"use client";

/**
 * Поля участника «Астана дарыны» в своём профиле: школа и Ф.И.О. преподавателя.
 *
 * Контракт:
 *  - показываются только тем, кто регистрировался по форме astana-daryny; признак —
 *    isAstanaDarynProfile(additional), те же ключи, что читает useDarynClassFilter;
 *  - школа берётся из публичного справочника astana-daryn/schools.json. Он отдаёт
 *    два разных набора: с параметром district — школы этого района (ученическая
 *    каскадная форма, id порядка 15000), без параметра — короткий справочник
 *    астанинских школ (id 1–326), на который ссылаются участники педолимпа.
 *    Поэтому район из профиля и решает, чем кормить селект. Каскад регион → район
 *    здесь намеренно не повторяется: в профиле меняют школу, а не место жительства;
 *  - руководителям ПедОлимпа школа не показывается: их форма школу не спрашивает и пишет 0;
 *  - вписанная для документов организация показывается вместо преподавателя и только для
 *    чтения: она лежит в том же базовом ключе (см. astanaDarynTeacher);
 *  - Ф.И.О. преподавателя показывается только тем, у кого преподаватель реально
 *    указан. У ученической формы поле обязательное, а участникам педолимпа его
 *    заполнять нечем — они сами учителя, и в профиле лежит заглушка "none";
 *  - базовое Ф.И.О. с регистрации только показывается: правка его ученику закрыта,
 *    иначе одна и та же строка уезжала бы во все дипломы сразу. Правка под каждый
 *    диплом теперь живёт во вкладке «Сертификаты» — CertificateTeacherDrawer;
 *  - компонент ничего не сохраняет сам: пишет в additional родителя, и тот уезжает
 *    в PUT /user.json вместе с остальным профилем.
 */

import * as React from "react";
import { useTranslations } from "next-intl";

import { Input } from "@/components/ui/input";
import { useLocale } from "@/hooks/use-locale";
import { useGetRegisterReferenceQuery } from "@/api/register/registerReferencePublicApi";
import { SearchableSelect } from "@/app/register/[slug]/SearchableSelect";
import {
  ASTANA_DARYN_ADMIN_DIRECTION,
  pickReferenceLabel,
  type ReferenceLabel,
} from "@/app/register/[slug]/registrationFormSchema";

import {
  readOrganization,
  readString,
  readTeacherName,
  type Additional,
} from "./astanaDarynTeacher";

/* Те же путь и колонки, что у поля school в ASTANA_DARYN_STUDENT_SCHEMA. */
const SCHOOLS_PATH = "astana-daryn/schools.json";
const SCHOOL_LABEL: ReferenceLabel = {
  default: "name_ru",
  byLocale: { kk: "name_kz" },
};

type Props = {
  additional: Additional;
  onChange: (next: Additional) => void;
};

const AstanaDarynFields: React.FC<Props> = ({ additional, onChange }) => {
  const t = useTranslations();
  const { currentLocale } = useLocale();

  const district = readString(additional, "astana_daryn_district");

  const { data: rows = [] } = useGetRegisterReferenceQuery({
    path: SCHOOLS_PATH,
    ...(district ? { params: { district } } : {}),
  });

  const options = React.useMemo(
    () =>
      rows.map((row) => ({
        value: Number(row.id),
        label: pickReferenceLabel(row, SCHOOL_LABEL, currentLocale),
      })),
    [rows, currentLocale]
  );

  const schoolId = Number(additional.astana_daryn_school_id);

  const organization = readOrganization(additional);
  const teacherName = organization ? null : readTeacherName(additional);

  const hasSchool =
    readString(additional, "astana_daryn_class_name") !== ASTANA_DARYN_ADMIN_DIRECTION;

  return (
    <div className="space-y-4">
      {hasSchool && (
        <div>
          <label className="text-sm font-medium mb-1 block">
            {t("ui-invite-form-fields-school")}
          </label>
          <SearchableSelect
            options={options}
            value={Number.isFinite(schoolId) ? schoolId : null}
            onChange={(next) =>
              onChange({ ...additional, astana_daryn_school_id: next })
            }
            placeholder={t("placeholder-select-school")}
            disabled={options.length === 0}
          />
        </div>
      )}

      {organization && (
        <div>
          <label className="text-sm font-medium mb-1 block">
            {t("label-organization")}
          </label>
          <Input value={organization} disabled />
          <div className="mt-1 text-xs text-muted-foreground">
            {t("hint-organization-locked")}
          </div>
        </div>
      )}

      {teacherName && (
        <>
          <div>
            <label className="text-sm font-medium mb-1 block">
              {t("label-teacher")}
            </label>
            {/* disabled — как у неизменяемых полей в CommonUserFields: значение
                приходит с регистрации, и правка его ученику закрыта. */}
            <Input value={teacherName} disabled />
            <div className="mt-1 text-xs text-muted-foreground">
              {t("hint-teacher-locked")}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AstanaDarynFields;
